<template>
  <div class="cash-control-shell">
    <!-- HERO -->
    <section class="cash-hero">
      <div>
        <p class="eyebrow">LotLedger Wine Bar</p>
        <h1>Cash Control</h1>
        <p class="hero-copy">
          Open the till, track cash movements, close the drawer, and compare Square totals against LotLedger sales.
        </p>
      </div>

      <div class="hero-date">
        <span>Service Date</span>
        <input
          type="date"
          v-model="serviceDate"
          @change="handleServiceDateChange"
        />
      </div>
    </section>

    <!-- DRAWER STATUS -->
    <section class="drawer-status-panel">
      <div class="drawer-status-left">
        <p class="eyebrow">Drawer Status</p>

        <h2>
          {{ drawerSession ? statusLabel(drawerSession.status) : 'No Drawer Open' }}
        </h2>

        <p v-if="drawerSession">
          Opened {{ formatDateTime(drawerSession.opened_at) }}
          <span v-if="drawerSession.opened_by">
            by {{ drawerSession.opened_by }}
          </span>
        </p>

        <p v-else>
          Open the till before service or use this page at closeout to reconcile the day.
        </p>
      </div>

      <div class="drawer-status-actions">
        <button
          v-if="!drawerSession || drawerSession.status === 'closed'"
          class="primary-action"
          @click="openOpenTillModal"
        >
          Open Till
        </button>

        <template v-else>
          <button
            class="ghost-action"
            @click="openMovementModal('cash_added')"
          >
            Add Cash
          </button>

          <button
            class="ghost-action"
            @click="openMovementModal('cash_removed')"
          >
            Remove Cash
          </button>

          <button
            class="primary-action"
            @click="openCloseTillModal"
          >
            Close Till
          </button>
        </template>
      </div>
    </section>

    <!-- SUMMARY CARDS -->
    <section class="summary-grid">
      <article class="summary-card">
        <span>Opening Cash</span>
        <strong>${{ money(summary.opening_cash) }}</strong>
      </article>

      <article class="summary-card">
        <span>Cash Sales</span>
        <strong>${{ money(summary.cash_sales_total) }}</strong>
      </article>

      <article class="summary-card">
        <span>Square Sales</span>
        <strong>${{ money(summary.square_sales_total) }}</strong>
      </article>

      <article class="summary-card">
        <span>Cash Added</span>
        <strong>${{ money(summary.cash_added) }}</strong>
      </article>

      <article class="summary-card">
        <span>Cash Removed</span>
        <strong>${{ money(summary.cash_removed) }}</strong>
      </article>

      <article class="summary-card gold">
        <span>Expected Cash</span>
        <strong>${{ money(expectedCash) }}</strong>
      </article>
    </section>

    <!-- CLOSEOUT PANEL -->
    <section class="closeout-grid">
      <article class="closeout-card">
        <div class="section-heading">
          <p class="eyebrow">Cash Drawer</p>
          <h2>Cash Balance</h2>
        </div>

        <div class="balance-readout">
          <div>
            <span>Expected Cash</span>
            <strong>${{ money(expectedCash) }}</strong>
          </div>

          <div>
            <span>Counted Cash</span>
            <strong>${{ money(summary.counted_cash) }}</strong>
          </div>

          <div :class="varianceClass(cashOverShort)">
            <span>Over / Short</span>
            <strong>{{ signedMoney(cashOverShort) }}</strong>
          </div>
        </div>

        <p class="readout-copy">
          Expected cash equals opening cash plus cash sales plus cash added minus cash removed.
        </p>
      </article>

      <article class="closeout-card">
        <div class="section-heading">
          <p class="eyebrow">Square Reconcile</p>
          <h2>Card Balance</h2>
        </div>

        <div class="balance-readout">
          <div>
            <span>LotLedger Square</span>
            <strong>${{ money(summary.square_sales_total) }}</strong>
          </div>

          <div>
            <span>Square Reported</span>
            <strong>${{ money(summary.square_reported_total) }}</strong>
          </div>

          <div :class="varianceClass(squareVariance)">
            <span>Variance</span>
            <strong>{{ signedMoney(squareVariance) }}</strong>
          </div>
        </div>

        <p class="readout-copy">
          Key the Square total from the Square app or batch report when closing the till.
        </p>
      </article>
    </section>

    <!-- MOVEMENTS -->
    <section class="movement-panel">
      <div class="history-header">
        <div>
          <p class="eyebrow">Till Activity</p>
          <h2>Cash Movements</h2>
        </div>

        <button class="ghost-action" @click="fetchCashDrawer">
          Refresh
        </button>
      </div>

      <div v-if="movements.length" class="movement-list">
        <article
          v-for="movement in movements"
          :key="movement.movement_id"
          class="movement-card"
        >
          <div>
            <p class="label">{{ formatMovementType(movement.movement_type) }}</p>
            <h3>{{ signedMoney(movementAmountSigned(movement)) }}</h3>
            <p>{{ movement.notes || 'No notes entered' }}</p>
          </div>

          <div class="movement-meta">
            <span>{{ movement.created_by || '—' }}</span>
            <strong>{{ formatDateTime(movement.created_at) }}</strong>
          </div>
        </article>
      </div>

      <div v-else class="empty-state">
        <h3>No cash movements yet</h3>
        <p>Cash added, removed, drops, and paid-outs will appear here.</p>
      </div>
    </section>

    <!-- OPEN TILL MODAL -->
    <div v-if="showOpenTillModal" class="modal-overlay">
      <div class="cash-modal small-modal">
        <div class="modal-topline"></div>

        <header class="modal-header">
          <div>
            <p class="eyebrow">Open Till</p>
            <h2>Start Drawer</h2>
            <p>Enter the starting cash in the drawer for {{ formatDate(serviceDate) }}.</p>
          </div>

          <button class="icon-close" @click="closeOpenTillModal">×</button>
        </header>

        <div class="modal-body">
          <section class="form-grid">
            <label>
              <span>Opening Cash</span>
              <input
                type="number"
                v-model.number="openTillForm.opening_cash"
                min="0"
                step="0.01"
                placeholder="200.00"
              />
            </label>

            <label>
              <span>Opened By</span>
              <input
                type="text"
                v-model="openTillForm.opened_by"
                placeholder="Cashier name"
              />
            </label>

            <label class="full-width">
              <span>Notes</span>
              <input
                type="text"
                v-model="openTillForm.notes"
                placeholder="Optional opening note"
              />
            </label>
          </section>

          <footer class="modal-actions">
            <button class="cancel-action" @click="closeOpenTillModal">
              Cancel
            </button>

            <button class="primary-action" @click="openTill">
              Open Till
            </button>
          </footer>
        </div>
      </div>
    </div>

    <!-- CASH MOVEMENT MODAL -->
    <div v-if="showMovementModal" class="modal-overlay">
      <div class="cash-modal small-modal">
        <div class="modal-topline amber"></div>

        <header class="modal-header">
          <div>
            <p class="eyebrow">Cash Movement</p>
            <h2>{{ movementModalTitle }}</h2>
            <p>Record cash added, removed, drops, paid-outs, or corrections.</p>
          </div>

          <button class="icon-close" @click="closeMovementModal">×</button>
        </header>

        <div class="modal-body">
          <section class="form-grid">
            <label>
              <span>Movement Type</span>
              <select v-model="movementForm.movement_type">
                <option value="cash_added">Cash Added</option>
                <option value="cash_removed">Cash Removed</option>
                <option value="cash_drop">Cash Drop</option>
                <option value="paid_out">Paid Out</option>
                <option value="correction">Correction</option>
              </select>
            </label>

            <label>
              <span>Amount</span>
              <input
                type="number"
                v-model.number="movementForm.amount"
                min="0"
                step="0.01"
                placeholder="0.00"
              />
            </label>

            <label>
              <span>Created By</span>
              <input
                type="text"
                v-model="movementForm.created_by"
                placeholder="Cashier name"
              />
            </label>

            <label class="full-width">
              <span>Notes</span>
              <input
                type="text"
                v-model="movementForm.notes"
                placeholder="Reason for movement"
              />
            </label>
          </section>

          <footer class="modal-actions">
            <button class="cancel-action" @click="closeMovementModal">
              Cancel
            </button>

            <button class="primary-action" @click="saveMovement">
              Save Movement
            </button>
          </footer>
        </div>
      </div>
    </div>

    <!-- CLOSE TILL MODAL -->
    <div v-if="showCloseTillModal" class="modal-overlay">
      <div class="cash-modal">
        <div class="modal-topline gold"></div>

        <header class="modal-header">
          <div>
            <p class="eyebrow">Close Till</p>
            <h2>Drawer Closeout</h2>
            <p>Count the drawer and enter the Square reported total for {{ formatDate(serviceDate) }}.</p>
          </div>

          <button class="icon-close" @click="closeCloseTillModal">×</button>
        </header>

        <div class="modal-body">
          <section class="close-preview-grid">
            <article>
              <span>Expected Cash</span>
              <strong>${{ money(expectedCash) }}</strong>
            </article>

            <article>
              <span>Counted Cash</span>
              <strong>${{ money(closeTillForm.counted_cash) }}</strong>
            </article>

            <article :class="varianceClass(closeCashOverShort)">
              <span>Cash Over / Short</span>
              <strong>{{ signedMoney(closeCashOverShort) }}</strong>
            </article>

            <article>
              <span>LotLedger Square</span>
              <strong>${{ money(summary.square_sales_total) }}</strong>
            </article>

            <article>
              <span>Square Reported</span>
              <strong>${{ money(closeTillForm.square_reported_total) }}</strong>
            </article>

            <article :class="varianceClass(closeSquareVariance)">
              <span>Square Variance</span>
              <strong>{{ signedMoney(closeSquareVariance) }}</strong>
            </article>
          </section>

          <section class="form-grid close-form">
            <label>
              <span>Counted Cash</span>
              <input
                type="number"
                v-model.number="closeTillForm.counted_cash"
                min="0"
                step="0.01"
                placeholder="0.00"
              />
            </label>

            <label>
              <span>Square Reported Total</span>
              <input
                type="number"
                v-model.number="closeTillForm.square_reported_total"
                min="0"
                step="0.01"
                placeholder="0.00"
              />
            </label>

            <label>
              <span>Closed By</span>
              <input
                type="text"
                v-model="closeTillForm.closed_by"
                placeholder="Cashier / manager name"
              />
            </label>

            <label class="full-width">
              <span>Notes</span>
              <input
                type="text"
                v-model="closeTillForm.notes"
                placeholder="Optional closeout notes"
              />
            </label>
          </section>

          <footer class="modal-actions">
            <button class="cancel-action" @click="closeCloseTillModal">
              Cancel
            </button>

            <button class="primary-action" @click="closeTill">
              Close Drawer
            </button>
          </footer>
        </div>
      </div>
    </div>

    <!-- WARNING MODAL -->
    <div v-if="showWarning" class="modal-overlay">
      <div class="cash-modal small-modal">
        <div class="modal-topline amber"></div>

        <header class="modal-header">
          <div>
            <p class="eyebrow">Heads Up</p>
            <h2>Check This</h2>
            <p>{{ warningMessage }}</p>
          </div>

          <button class="icon-close" @click="showWarning = false">×</button>
        </header>

        <div class="modal-body">
          <button class="primary-action full-button" @click="showWarning = false">
            OK
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';

export default {
  name: 'CashControlPage',

  data() {
    return {
      serviceDate: new Date().toISOString().split('T')[0],

      drawerSession: null,
      summary: {
        opening_cash: 0,
        cash_sales_total: 0,
        square_sales_total: 0,
        cash_added: 0,
        cash_removed: 0,
        expected_cash: 0,
        counted_cash: 0,
        square_reported_total: 0,
        square_variance: 0,
        cash_over_short: 0
      },

      movements: [],

      showOpenTillModal: false,
      openTillForm: {
        opening_cash: 200,
        opened_by: '',
        notes: ''
      },

      showMovementModal: false,
      movementForm: {
        movement_type: 'cash_added',
        amount: 0,
        created_by: '',
        notes: ''
      },

      showCloseTillModal: false,
      closeTillForm: {
        counted_cash: 0,
        square_reported_total: 0,
        closed_by: '',
        notes: ''
      },

      showWarning: false,
      warningMessage: ''
    };
  },

  computed: {
    expectedCash() {
      return Number(this.summary.expected_cash ?? (
        Number(this.summary.opening_cash || 0) +
        Number(this.summary.cash_sales_total || 0) +
        Number(this.summary.cash_added || 0) -
        Number(this.summary.cash_removed || 0)
      ));
    },

    cashOverShort() {
      return Number(this.summary.cash_over_short ?? (
        Number(this.summary.counted_cash || 0) - Number(this.expectedCash || 0)
      ));
    },

    squareVariance() {
      return Number(this.summary.square_variance ?? (
        Number(this.summary.square_reported_total || 0) -
        Number(this.summary.square_sales_total || 0)
      ));
    },

    closeCashOverShort() {
      return Number(this.closeTillForm.counted_cash || 0) - Number(this.expectedCash || 0);
    },

    closeSquareVariance() {
      return Number(this.closeTillForm.square_reported_total || 0) -
        Number(this.summary.square_sales_total || 0);
    },

    movementModalTitle() {
      return this.formatMovementType(this.movementForm.movement_type);
    }
  },

  mounted() {
    this.fetchCashDrawer();
  },

  methods: {
    async handleServiceDateChange() {
      await this.fetchCashDrawer();
    },

    normalizeDrawerResponse(data) {
      const session =
        data?.session ||
        data?.drawer_session ||
        data?.drawerSession ||
        (data?.drawer_session_id ? data : null);

      const summary =
        data?.summary ||
        data?.totals ||
        session ||
        {};

      const movements =
        data?.movements ||
        data?.cash_movements ||
        [];

      return {
        session,
        summary,
        movements
      };
    },

    async fetchCashDrawer() {
      try {
        const response = await api.get('/cash-drawer', {
          params: {
            date: this.serviceDate
          }
        });

        const normalized = this.normalizeDrawerResponse(response.data || {});

        this.drawerSession = normalized.session;
        this.summary = {
          opening_cash: Number(normalized.summary.opening_cash || 0),
          cash_sales_total: Number(normalized.summary.cash_sales_total || 0),
          square_sales_total: Number(normalized.summary.square_sales_total || 0),
          cash_added: Number(normalized.summary.cash_added || 0),
          cash_removed: Number(normalized.summary.cash_removed || 0),
          expected_cash: Number(normalized.summary.expected_cash || 0),
          counted_cash: Number(normalized.summary.counted_cash || 0),
          square_reported_total: Number(normalized.summary.square_reported_total || 0),
          square_variance: Number(normalized.summary.square_variance || 0),
          cash_over_short: Number(normalized.summary.cash_over_short || 0)
        };

        this.movements = normalized.movements || [];
      } catch (error) {
        if (error.response?.status === 404) {
          this.drawerSession = null;
          this.movements = [];
          this.summary = {
            opening_cash: 0,
            cash_sales_total: 0,
            square_sales_total: 0,
            cash_added: 0,
            cash_removed: 0,
            expected_cash: 0,
            counted_cash: 0,
            square_reported_total: 0,
            square_variance: 0,
            cash_over_short: 0
          };
          return;
        }

        console.error('❌ Error fetching cash drawer:', error);
        this.showWarningPopup(
          error.response?.data?.details ||
          error.response?.data?.error ||
          'Unable to load cash drawer.'
        );
      }
    },

    openOpenTillModal() {
      this.openTillForm = {
        opening_cash: 200,
        opened_by: '',
        notes: ''
      };

      this.showOpenTillModal = true;
    },

    closeOpenTillModal() {
      this.showOpenTillModal = false;
      this.openTillForm = {
        opening_cash: 200,
        opened_by: '',
        notes: ''
      };
    },

    async openTill() {
      if (Number(this.openTillForm.opening_cash || 0) < 0) {
        this.showWarningPopup('Opening cash cannot be negative.');
        return;
      }

      try {
        await api.post('/cash-drawer/open', {
          service_date: this.serviceDate,
          opening_cash: Number(this.openTillForm.opening_cash || 0),
          opened_by: this.openTillForm.opened_by || null,
          notes: this.openTillForm.notes || null
        });

        this.closeOpenTillModal();
        await this.fetchCashDrawer();
      } catch (error) {
        console.error('❌ Error opening till:', error);
        this.showWarningPopup(
          error.response?.data?.details ||
          error.response?.data?.error ||
          'Unable to open till.'
        );
      }
    },

    openMovementModal(type = 'cash_added') {
      if (!this.drawerSession?.drawer_session_id) {
        this.showWarningPopup('Open the till before adding cash movements.');
        return;
      }

      this.movementForm = {
        movement_type: type,
        amount: 0,
        created_by: '',
        notes: ''
      };

      this.showMovementModal = true;
    },

    closeMovementModal() {
      this.showMovementModal = false;
      this.movementForm = {
        movement_type: 'cash_added',
        amount: 0,
        created_by: '',
        notes: ''
      };
    },

    async saveMovement() {
      if (!this.drawerSession?.drawer_session_id) {
        this.showWarningPopup('No open drawer session found.');
        return;
      }

      if (Number(this.movementForm.amount || 0) <= 0) {
        this.showWarningPopup('Movement amount must be greater than zero.');
        return;
      }

      try {
        await api.post(`/cash-drawer/${this.drawerSession.drawer_session_id}/movement`, {
          movement_type: this.movementForm.movement_type,
          amount: Number(this.movementForm.amount || 0),
          created_by: this.movementForm.created_by || null,
          notes: this.movementForm.notes || null
        });

        this.closeMovementModal();
        await this.fetchCashDrawer();
      } catch (error) {
        console.error('❌ Error saving cash movement:', error);
        this.showWarningPopup(
          error.response?.data?.details ||
          error.response?.data?.error ||
          'Unable to save cash movement.'
        );
      }
    },

    openCloseTillModal() {
      if (!this.drawerSession?.drawer_session_id) {
        this.showWarningPopup('No open drawer session found.');
        return;
      }

      this.closeTillForm = {
        counted_cash: Number(this.expectedCash || 0),
        square_reported_total: Number(this.summary.square_sales_total || 0),
        closed_by: '',
        notes: ''
      };

      this.showCloseTillModal = true;
    },

    closeCloseTillModal() {
      this.showCloseTillModal = false;
      this.closeTillForm = {
        counted_cash: 0,
        square_reported_total: 0,
        closed_by: '',
        notes: ''
      };
    },

    async closeTill() {
      if (!this.drawerSession?.drawer_session_id) {
        this.showWarningPopup('No open drawer session found.');
        return;
      }

      if (Number(this.closeTillForm.counted_cash || 0) < 0) {
        this.showWarningPopup('Counted cash cannot be negative.');
        return;
      }

      if (Number(this.closeTillForm.square_reported_total || 0) < 0) {
        this.showWarningPopup('Square reported total cannot be negative.');
        return;
      }

      try {
        await api.post(`/cash-drawer/${this.drawerSession.drawer_session_id}/close`, {
          counted_cash: Number(this.closeTillForm.counted_cash || 0),
          square_reported_total: Number(this.closeTillForm.square_reported_total || 0),
          closed_by: this.closeTillForm.closed_by || null,
          notes: this.closeTillForm.notes || null
        });

        this.closeCloseTillModal();
        await this.fetchCashDrawer();

        this.showWarningPopup('Drawer closed successfully.');
      } catch (error) {
        console.error('❌ Error closing till:', error);
        this.showWarningPopup(
          error.response?.data?.details ||
          error.response?.data?.error ||
          'Unable to close till.'
        );
      }
    },

    movementAmountSigned(movement) {
      const amount = Number(movement.amount || 0);
      const type = movement.movement_type;

      if (
        type === 'cash_removed' ||
        type === 'cash_drop' ||
        type === 'paid_out'
      ) {
        return -amount;
      }

      return amount;
    },

    formatMovementType(type) {
      const labels = {
        cash_added: 'Cash Added',
        cash_removed: 'Cash Removed',
        cash_drop: 'Cash Drop',
        paid_out: 'Paid Out',
        correction: 'Correction'
      };

      return labels[type] || type || 'Movement';
    },

    statusLabel(status) {
      const labels = {
        open: 'Drawer Open',
        closed: 'Drawer Closed'
      };

      return labels[status] || status || 'Unknown';
    },

    varianceClass(value) {
      const amount = Number(value || 0);

      return {
        'variance-good': amount === 0,
        'variance-over': amount > 0,
        'variance-short': amount < 0
      };
    },

    money(value) {
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(Number(value || 0));
    },

    signedMoney(value) {
      const amount = Number(value || 0);
      const prefix = amount > 0 ? '+' : amount < 0 ? '-' : '';

      return `${prefix}$${this.money(Math.abs(amount))}`;
    },

    formatDate(date) {
      if (!date) return '';
      return new Date(date).toISOString().split('T')[0];
    },

    formatDateTime(value) {
      if (!value) return '—';

      return new Date(value).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      });
    },

    showWarningPopup(message) {
      this.warningMessage = message;
      this.showWarning = true;
    }
  }
};
</script>

<style scoped>
.cash-control-shell {
  min-height: 100vh;
  padding: 28px;
  color: #fff8f1;
  font-family: "Poppins", sans-serif;
  background:
    radial-gradient(circle at top left, rgba(128, 18, 54, 0.28), transparent 34%),
    linear-gradient(135deg, #120b10, #22131a 48%, #0f0a0d);
}

.cash-hero,
.drawer-status-panel,
.summary-card,
.closeout-card,
.movement-panel,
.movement-card {
  border: 1px solid rgba(255, 255, 255, 0.095);
  background: rgba(255, 255, 255, 0.065);
  box-shadow: 0 20px 55px rgba(0, 0, 0, 0.22);
}

.cash-hero {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  align-items: flex-end;
  padding: 26px;
  margin-bottom: 18px;
  border-radius: 30px;
  background:
    linear-gradient(135deg, rgba(255, 255, 255, 0.09), rgba(255, 255, 255, 0.035));
}

.eyebrow,
.label {
  margin: 0 0 8px;
  color: #c8a66a;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.cash-hero h1 {
  margin: 0;
  font-size: clamp(42px, 6vw, 82px);
  letter-spacing: -0.08em;
  line-height: 0.9;
}

.hero-copy {
  max-width: 680px;
  margin: 14px 0 0;
  color: #d8cbc4;
  line-height: 1.5;
}

.hero-date {
  min-width: 220px;
  display: grid;
  gap: 8px;
}

.hero-date span {
  color: #b9aaa4;
  font-size: 12px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.1em;
}

input,
select {
  min-height: 44px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 14px;
  background: rgba(8, 5, 8, 0.58);
  color: #fff8f1;
  padding: 0 14px;
  outline: none;
}

.drawer-status-panel {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 22px;
  padding: 22px;
  margin-bottom: 16px;
  border-radius: 28px;
}

.drawer-status-left h2 {
  margin: 0;
  font-size: clamp(34px, 4vw, 58px);
  line-height: 0.95;
  letter-spacing: -0.07em;
}

.drawer-status-left p {
  margin: 10px 0 0;
  color: #cfc2bd;
}

.drawer-status-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: flex-end;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(140px, 1fr));
  gap: 14px;
  margin-bottom: 16px;
}

.summary-card {
  padding: 18px;
  border-radius: 24px;
}

.summary-card span,
.balance-readout span,
.close-preview-grid span {
  display: block;
  color: #b9aaa4;
  font-size: 11px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.1em;
}

.summary-card strong {
  display: block;
  margin-top: 8px;
  font-size: 27px;
  letter-spacing: -0.05em;
}

.summary-card.gold strong {
  color: #f1c979;
}

.closeout-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(280px, 1fr));
  gap: 16px;
  margin-bottom: 16px;
}

.closeout-card {
  padding: 22px;
  border-radius: 28px;
}

.section-heading h2,
.history-header h2 {
  margin: 0;
  font-size: 34px;
  line-height: 0.95;
  letter-spacing: -0.06em;
}

.balance-readout {
  display: grid;
  gap: 12px;
  margin-top: 18px;
}

.balance-readout div {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: center;
  padding: 14px;
  border-radius: 18px;
  background: rgba(0, 0, 0, 0.22);
}

.balance-readout strong {
  font-size: 24px;
  letter-spacing: -0.04em;
}

.readout-copy {
  margin: 16px 0 0;
  color: #cfc2bd;
  line-height: 1.5;
}

.variance-good strong {
  color: #c9f5c9;
}

.variance-over strong {
  color: #f1c979;
}

.variance-short strong {
  color: #ff8e7a;
}

.movement-panel {
  padding: 22px;
  border-radius: 28px;
}

.history-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 14px;
  margin-bottom: 16px;
}

.movement-list {
  display: grid;
  gap: 12px;
}

.movement-card {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 16px;
  border-radius: 20px;
}

.movement-card h3 {
  margin: 0;
  font-size: 26px;
  letter-spacing: -0.05em;
}

.movement-card p {
  margin: 8px 0 0;
  color: #cfc2bd;
}

.movement-meta {
  display: grid;
  gap: 6px;
  text-align: right;
  color: #b9aaa4;
}

.movement-meta strong {
  color: #fff8f1;
}

.empty-state {
  padding: 28px;
  border-radius: 24px;
  text-align: center;
  background: rgba(255, 255, 255, 0.045);
}

.empty-state h3 {
  margin: 0 0 8px;
  font-size: 26px;
  letter-spacing: -0.04em;
}

.empty-state p {
  margin: 0;
  color: #cfc2bd;
}

.primary-action,
.ghost-action,
.cancel-action {
  min-height: 44px;
  padding: 0 16px;
  border-radius: 999px;
  border: 0;
  cursor: pointer;
  font-weight: 900;
}

.primary-action {
  color: #1b1015;
  background: linear-gradient(135deg, #f1c979, #c8a66a);
}

.ghost-action {
  color: #fff8f1;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
}

.cancel-action {
  color: #fff8f1;
  background: rgba(255, 255, 255, 0.08);
}

.full-button {
  width: 100%;
}

/* ---------- Modals ---------- */

.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 3000;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 26px;
  overflow: hidden;
  background:
    radial-gradient(circle at top left, rgba(128, 18, 54, 0.34), transparent 36%),
    rgba(5, 3, 5, 0.78);
  backdrop-filter: blur(12px);
}

.cash-modal {
  width: min(760px, 94vw);
  max-height: calc(100vh - 52px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border-radius: 30px;
  background:
    linear-gradient(145deg, rgba(35, 22, 29, 0.98), rgba(14, 10, 14, 0.98));
  border: 1px solid rgba(255, 255, 255, 0.11);
  color: #f7efe8;
  box-shadow: 0 34px 90px rgba(0, 0, 0, 0.55);
}

.small-modal {
  width: min(560px, 94vw);
}

.modal-topline {
  flex: 0 0 auto;
  height: 5px;
  background: linear-gradient(90deg, #8f123f, #c24a2f, #c8a66a);
}

.modal-topline.gold {
  background: linear-gradient(90deg, #c8a66a, #f1c979);
}

.modal-topline.amber {
  background: linear-gradient(90deg, #c8a66a, #d77a2d);
}

.modal-header {
  flex: 0 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 18px;
  padding: 26px 28px 18px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.modal-header h2 {
  margin: 0;
  font-size: 36px;
  line-height: 0.95;
  letter-spacing: -0.06em;
}

.modal-header p {
  margin: 10px 0 0;
  color: #cfc2bd;
  line-height: 1.45;
}

.icon-close {
  width: 46px;
  height: 46px;
  flex: 0 0 46px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.07);
  color: #fff8f1;
  font-size: 30px;
  line-height: 1;
  cursor: pointer;
}

.modal-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 24px 28px 28px;
}

.modal-body::-webkit-scrollbar {
  width: 10px;
}

.modal-body::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.06);
  border-radius: 999px;
}

.modal-body::-webkit-scrollbar-thumb {
  background: rgba(200, 166, 106, 0.45);
  border-radius: 999px;
}

.modal-body::-webkit-scrollbar-thumb:hover {
  background: rgba(200, 166, 106, 0.65);
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(180px, 1fr));
  gap: 14px;
}

.form-grid label {
  display: grid;
  gap: 8px;
}

.form-grid label span {
  color: #b9aaa4;
  font-size: 12px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.1em;
}

.full-width {
  grid-column: 1 / -1;
}

.close-preview-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(160px, 1fr));
  gap: 12px;
  margin-bottom: 18px;
}

.close-preview-grid article {
  padding: 14px;
  border-radius: 18px;
  background: rgba(0, 0, 0, 0.24);
}

.close-preview-grid strong {
  display: block;
  margin-top: 8px;
  font-size: 23px;
  letter-spacing: -0.05em;
}

.close-form {
  margin-top: 10px;
}

.modal-actions {
  position: sticky;
  bottom: 0;
  z-index: 3;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin: 20px -28px -28px;
  padding: 18px 28px 24px;
  background:
    linear-gradient(
      180deg,
      rgba(14, 10, 14, 0),
      rgba(14, 10, 14, 0.94) 30%,
      rgba(14, 10, 14, 1)
    );
}

@media (max-width: 1100px) {
  .summary-grid {
    grid-template-columns: repeat(3, minmax(140px, 1fr));
  }
}

@media (max-width: 820px) {
  .cash-control-shell {
    padding: 14px;
  }

  .cash-hero,
  .drawer-status-panel,
  .history-header,
  .movement-card {
    flex-direction: column;
    align-items: stretch;
  }

  .hero-date {
    width: 100%;
  }

  .drawer-status-actions {
    justify-content: stretch;
  }

  .drawer-status-actions button {
    flex: 1;
  }

  .summary-grid,
  .closeout-grid,
  .form-grid,
  .close-preview-grid {
    grid-template-columns: 1fr;
  }

  .modal-overlay {
    align-items: stretch;
    padding: 12px;
  }

  .cash-modal {
    width: 100%;
    max-height: calc(100vh - 24px);
  }

  .modal-header {
    padding: 22px 20px 16px;
  }

  .modal-body {
    padding: 20px;
  }

  .modal-actions {
    margin: 20px -20px -20px;
    padding: 18px 20px 22px;
  }
}
</style>